import { useEffect, useRef, useState } from "react";
import { User, LogOutIcon, SettingsIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/utils";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import { useAuth } from "../../lib/AuthContext";
import DoctorModeSwitch from "./DoctorModeSwitch";

function getRoleLabel(role, t) {
  const labels = {
    doctor: t("app.register.roleDoctorLabel", "Médico"),
    patient: t("app.register.rolePatientLabel", "Paciente"),
    secretary: t("app.register.roleSecretaryLabel", "Secretaria"),
    association: t("app.register.roleAssociationLabel", "Asociación"),
    institution: t("app.register.institutionLabel", "Institución"),
    admin: t("common.admin", "Admin"),
  };
  return labels[role] || role;
}

export default function UserMenu({ variant = "light" }) {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const isLight = variant === "light";

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
          isLight
            ? "text-white hover:bg-white/15"
            : "text-gray-600 hover:bg-gray-100"
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("common.myAccount", "Mi cuenta")}
      >
        <User className="h-6 w-6" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 shadow-lg"
          role="menu"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {getRoleLabel(user.role, t)}
            </p>
            <p className="mt-1 break-all text-xs text-gray-600">{user.email}</p>
          </div>

          {user.role === "doctor" && (
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 mb-1">
                {t("home.mode.operateAs", "Operar como")}
              </p>
              <DoctorModeSwitch
                onChange={() => {
                  setOpen(false);
                  navigate("/");
                }}
              />
            </div>
          )}

          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
            >
              <SettingsIcon className="h-4 w-4 shrink-0" />
              {t("common.profile", "Perfil")}
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
              onClick={() => {
                setOpen(false);
                window.location.href = "/logout";
              }}
            >
              <LogOutIcon className="h-4 w-4 shrink-0" />
              {t("common.logout", "Cerrar sesión")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
